import { useCallback, useEffect, useState } from 'react';
import caseOpenedSample from './sounds/dvd-case-open.mp3';
import caseShutSample from './sounds/dvd-case-close.mp3';

export type SoundName =
  | 'pointerCrossedTarget'
  | 'targetPressed'
  | 'sceneChanged'
  | 'purchaseStamped'
  | 'ownershipGranted'
  | 'requestRefused'
  | 'installFinished'
  | 'identSounded'
  | 'caseOpened'
  | 'caseShut'
  | 'boxSetDown';

interface ToneLayer {
  waveform: OscillatorType;
  fromHz: number;
  toHz: number;
  peakGain: number;
  startAfterSeconds: number;
  durationSeconds: number;
}

interface NoiseLayer {
  fromCutoffHz: number;
  toCutoffHz: number;
  peakGain: number;
  durationSeconds: number;
  /**
   * Where this layer sits in the voice, like a tone's.
   *
   * Added for the DVD case, which is two noises rather than one: the clips let
   * go, and then the hinge flexes as the lid comes over. One event followed by
   * another is what a plastic case sounds like, and a single band could only be
   * one of them.
   */
  startAfterSeconds?: number;
  /**
   * How narrow the band is. Higher is more like a pitch and less like a hiss.
   *
   * The default is the wide, airy 0.9 the scene change uses. Plastic is the
   * other end of it: a crack is a narrow band of noise, which is why it reads as
   * a material and not as static.
   */
  resonance?: number;
}

interface SoundVoice {
  tones: readonly ToneLayer[];
  noises?: readonly NoiseLayer[];
}

const DEFAULT_NOISE_RESONANCE = 0.9;

const tone = (
  waveform: OscillatorType,
  fromHz: number,
  toHz: number,
  peakGain: number,
  durationSeconds: number,
  startAfterSeconds = 0,
): ToneLayer => ({ waveform, fromHz, toHz, peakGain, durationSeconds, startAfterSeconds });

const SOUND_LIBRARY: Record<SoundName, SoundVoice> = {
  pointerCrossedTarget: {
    tones: [tone('sine', 2400, 2050, 0.05, 0.04)],
  },
  targetPressed: {
    tones: [tone('square', 880, 620, 0.1, 0.06), tone('sine', 1760, 1760, 0.035, 0.03)],
  },
  sceneChanged: {
    tones: [],
    noises: [{ fromCutoffHz: 420, toCutoffHz: 6400, peakGain: 0.07, durationSeconds: 0.34 }],
  },
  purchaseStamped: {
    tones: [tone('square', 150, 68, 0.3, 0.2), tone('sine', 1500, 1180, 0.07, 0.13, 0.01)],
  },
  ownershipGranted: {
    tones: [
      tone('sine', 392, 392, 0.13, 0.62),
      tone('sine', 587, 587, 0.1, 0.58, 0.08),
      tone('sine', 784, 784, 0.08, 0.54, 0.16),
      tone('triangle', 196, 196, 0.07, 0.7),
    ],
  },
  requestRefused: {
    tones: [tone('square', 184, 116, 0.18, 0.24)],
  },
  installFinished: {
    tones: [
      tone('triangle', 523, 523, 0.13, 0.22),
      tone('triangle', 659, 659, 0.13, 0.22, 0.09),
      tone('triangle', 784, 784, 0.15, 0.42, 0.18),
    ],
  },
  /**
   * A DVD case being opened.
   *
   * Three things happen, in this order, and all three are needed or it is a
   * click rather than a case: the two clips along the open edge let go, which is
   * a hard narrow crack near the top of the range; the shell flexes, which is a
   * low body note with almost no attack; and the hinge creaks as the lid comes
   * over, which is a narrow band falling away over a quarter of a second.
   *
   * The crack leads by nothing at all and the creak follows it — they overlap,
   * because in a real case the lid is already moving when the clips release.
   */
  caseOpened: {
    tones: [
      // The shell taking the strain. Almost subsonic, and it is what gives the
      // sound a size: without it the crack could be a switch on a desk.
      tone('triangle', 224, 92, 0.085, 0.17),
      // The clips themselves, as pitch rather than noise. Very short.
      tone('square', 1480, 940, 0.028, 0.028),
    ],
    noises: [
      {
        fromCutoffHz: 5200,
        toCutoffHz: 2600,
        peakGain: 0.09,
        durationSeconds: 0.05,
        resonance: 2.2,
      },
      {
        fromCutoffHz: 1500,
        toCutoffHz: 520,
        peakGain: 0.045,
        durationSeconds: 0.26,
        startAfterSeconds: 0.045,
        resonance: 3.6,
      },
    ],
  },
  /**
   * And shut again: the same clips, taken the other way.
   *
   * Shorter and harder than opening, with no creak after it. Closing a case is
   * one event — the lid meets the base and the clips take — where opening is a
   * release followed by a movement.
   */
  caseShut: {
    tones: [tone('triangle', 186, 84, 0.1, 0.11)],
    noises: [
      { fromCutoffHz: 3400, toCutoffHz: 1500, peakGain: 0.1, durationSeconds: 0.045, resonance: 2 },
    ],
  },
  boxSetDown: {
    tones: [tone('sine', 134, 56, 0.15, 0.12), tone('triangle', 78, 42, 0.09, 0.18)],
    noises: [
      {
        fromCutoffHz: 1900,
        toCutoffHz: 420,
        peakGain: 0.055,
        durationSeconds: 0.07,
        resonance: 1.4,
      },
    ],
  },
  identSounded: {
    tones: [
      tone('triangle', 330, 330, 0.11, 0.18),
      tone('triangle', 392, 392, 0.11, 0.18, 0.1),
      tone('triangle', 494, 494, 0.11, 0.18, 0.2),
      tone('triangle', 587, 587, 0.11, 0.18, 0.3),
      tone('triangle', 698, 698, 0.16, 0.7, 0.4),
      tone('sine', 174, 174, 0.09, 1.1),
    ],
  },
};

/**
 * The two voices that are recordings rather than arithmetic.
 *
 * **Everything else in this library is synthesised, and the case is the reason
 * that stops being enough.** A hard narrow band of noise over a low body note is
 * a serviceable *click* and it is not a DVD case: what a case actually does is
 * two clips letting go a few milliseconds apart, a shell that rings slightly,
 * and a hinge that scrapes — a set of overlapping transients nobody reproduces
 * with three oscillators. The rest of the product's sounds are abstract
 * feedback, and an abstract sound is better synthesised than sampled. This one
 * is a physical object being handled, and there is no substitute for a
 * recording.
 *
 * Imported rather than pointed at in `public/`, so the bundler fingerprints them
 * and a replacement recording cannot be served from a stale cache. They are
 * about fifty kilobytes each and only ever fetched by somebody who has opened
 * their library with sound on.
 */
/**
 * Which bang in a recording is the one the picture has to agree with.
 *
 * **A recording of a thing happening is rarely one sound, and which of them
 * matters depends on the thing.** Opening a case is the clips letting go and
 * then a lid swinging: the bang is the *first* event and everything after it is
 * the aftermath. Closing one is a lid swinging and then the clips taking: the
 * bang is the *last* event and everything before it is the approach.
 *
 * That is not a detail. The close recording has three transients — the halves
 * meeting at 0.26, a settle at 0.50, the clips home at 0.86 — and lining the
 * loudest of them up with the picture put the sound a listener actually reads as
 * *shut* six tenths of a second late. The open recording has one, which is why
 * it was right without anybody thinking about it.
 */
type Align = 'first' | 'last';

const SAMPLES: Partial<Record<SoundName, { url: string; align: Align }>> = {
  caseOpened: { url: caseOpenedSample, align: 'first' },
  caseShut: { url: caseShutSample, align: 'last' },
};

/**
 * What a recording is levelled to when it plays.
 *
 * **Not a fixed gain, because the two recordings are not at the same level.**
 * The case opening peaks at 0.066 and the case shutting at 0.216 — three and a
 * half times louder — so one number applied to both leaves one of them inaudible
 * next to the interface's own clicks and the other jumping out of the page. Each
 * is scaled by what it actually peaks at, which is measured when it is decoded.
 *
 * 0.3 is where the loudest synthesised voice in this file sits, so a recording
 * lands among them rather than over them.
 */
const SAMPLE_PEAK = 0.3;

/**
 * How loud a moment has to be to count as a bang at all.
 *
 * Low enough to find the clips taking at the end of the closing recording, which
 * is half the level of the halves meeting at the start of it, and high enough
 * not to call the room tone between them a third one.
 */
const TRANSIENT_SHARE = 0.35;
/** Ten milliseconds, which is fine enough to separate two knocks. */
const FRAME_SECONDS = 0.01;

/**
 * How much of a transient's own peak counts as the start of it.
 *
 * A crack does not reach its maximum instantly, and lining a *maximum* up with a
 * picture puts the sound a few milliseconds late — what a listener hears is the
 * attack, not the top of it. Seven tenths is where the edge is steep enough to
 * be unambiguous, and the backoff carries the very start of the attack with it
 * so nothing is shaved off the front.
 */
const ONSET_SHARE = 0.7;
const ONSET_BACKOFF = 0.006;

/** Never so loud that the rest of the recording clips to get one bang up. */
const SAMPLE_CEILING = 0.9;

/**
 * A decoded recording, and the two things measured off it.
 *
 * `peakAt` is how far into the file its transient starts, which is what makes
 * scheduling possible: to put a crack on a given frame you start the file that
 * much *before* the frame. Both recordings have most of half a second of
 * handling noise before the clips go, and playing them from the beginning at the
 * moment a case opens is what put the sound behind the picture.
 */
interface Sample {
  buffer: AudioBuffer;
  peakAt: number;
  gain: number;
}

const decoded = new Map<string, Sample>();
const decoding = new Map<string, Promise<Sample | null>>();

const SOUND_PREFERENCE_STORAGE_KEY = 'gb.sound.enabled';
const SILENCE_FLOOR_GAIN = 0.0001;
const NOISE_BUFFER_SECONDS = 1;

function readStoredSoundPreference(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SOUND_PREFERENCE_STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

let soundIsEnabled = readStoredSoundPreference();
let sharedAudioContext: AudioContext | null = null;
let sharedNoiseBuffer: AudioBuffer | null = null;
const soundPreferenceListeners = new Set<(enabled: boolean) => void>();

function resolveAudioContextConstructor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  const legacy = (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  return window.AudioContext ?? legacy ?? null;
}

function openAudioContext(): AudioContext | null {
  const AudioContextConstructor = resolveAudioContextConstructor();
  if (!AudioContextConstructor) return null;

  sharedAudioContext ??= new AudioContextConstructor();
  if (sharedAudioContext.state === 'suspended') void sharedAudioContext.resume();
  return sharedAudioContext;
}

function noiseBufferFor(audio: AudioContext): AudioBuffer {
  if (sharedNoiseBuffer) return sharedNoiseBuffer;

  const sampleCount = Math.floor(audio.sampleRate * NOISE_BUFFER_SECONDS);
  const buffer = audio.createBuffer(1, sampleCount, audio.sampleRate);
  const samples = buffer.getChannelData(0);
  for (let index = 0; index < sampleCount; index += 1) samples[index] = Math.random() * 2 - 1;

  sharedNoiseBuffer = buffer;
  return buffer;
}

function scheduleEnvelope(
  gain: GainNode,
  startAt: number,
  peakGain: number,
  durationSeconds: number,
): void {
  const attackSeconds = Math.min(0.012, durationSeconds / 3);
  gain.gain.setValueAtTime(SILENCE_FLOOR_GAIN, startAt);
  gain.gain.linearRampToValueAtTime(peakGain, startAt + attackSeconds);
  gain.gain.exponentialRampToValueAtTime(SILENCE_FLOOR_GAIN, startAt + durationSeconds);
}

function playToneLayer(audio: AudioContext, layer: ToneLayer, playheadSeconds: number): void {
  const startAt = playheadSeconds + layer.startAfterSeconds;
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();

  oscillator.type = layer.waveform;
  oscillator.frequency.setValueAtTime(layer.fromHz, startAt);
  if (layer.toHz !== layer.fromHz) {
    oscillator.frequency.exponentialRampToValueAtTime(layer.toHz, startAt + layer.durationSeconds);
  }

  scheduleEnvelope(gain, startAt, layer.peakGain, layer.durationSeconds);

  oscillator.connect(gain).connect(audio.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + layer.durationSeconds + 0.02);
}

function playNoiseLayer(audio: AudioContext, layer: NoiseLayer, playheadSeconds: number): void {
  const startAt = playheadSeconds + (layer.startAfterSeconds ?? 0);
  const source = audio.createBufferSource();
  const bandpass = audio.createBiquadFilter();
  const gain = audio.createGain();

  source.buffer = noiseBufferFor(audio);
  bandpass.type = 'bandpass';
  bandpass.Q.value = layer.resonance ?? DEFAULT_NOISE_RESONANCE;
  bandpass.frequency.setValueAtTime(layer.fromCutoffHz, startAt);
  bandpass.frequency.exponentialRampToValueAtTime(
    layer.toCutoffHz,
    startAt + layer.durationSeconds,
  );

  scheduleEnvelope(gain, startAt, layer.peakGain, layer.durationSeconds);

  source.connect(bandpass).connect(gain).connect(audio.destination);
  source.start(startAt);
  source.stop(startAt + layer.durationSeconds + 0.02);
}

/**
 * Fetch and decode a recording, once.
 *
 * Both maps are needed and they are not the same thing: `decoded` is what can be
 * played this instant, and `decoding` is what stops a case opened three times in
 * a second from starting three downloads of the same file.
 *
 * A failure resolves to null rather than rejecting. There is a synthesised voice
 * behind every sampled one, so a recording that will not load costs the reader
 * the better sound and nothing else — and a network error on a foley file is
 * not something to put in front of anybody.
 */
/**
 * Where a recording's loudest transient begins, and what it peaks at.
 *
 * Measured rather than written down, so replacing a recording needs no second
 * edit anywhere: swap the file and the scheduling and the level follow it. The
 * two passes are cheap — a second and a half of mono at 44.1kHz is sixty-odd
 * thousand samples, once, on decode.
 */
function measure(buffer: AudioBuffer, align: Align): { peakAt: number; gain: number } {
  const samples = buffer.getChannelData(0);
  const rate = buffer.sampleRate;
  const frame = Math.max(1, Math.round(rate * FRAME_SECONDS));

  /* An envelope, coarse enough that one knock is one entry. */
  const envelope: number[] = [];
  let loudest = 0;
  for (let at = 0; at + frame <= samples.length; at += frame) {
    let level = 0;
    for (let index = at; index < at + frame; index += 1) {
      const value = Math.abs(samples[index] ?? 0);
      if (value > level) level = value;
    }
    envelope.push(level);
    if (level > loudest) loudest = level;
  }

  if (loudest === 0) return { peakAt: 0, gain: 1 };

  /* Every run of frames loud enough to be a bang rather than the room. */
  const bangs: { from: number; to: number }[] = [];
  let run: { from: number; to: number } | null = null;
  for (let index = 0; index < envelope.length; index += 1) {
    if ((envelope[index] ?? 0) >= loudest * TRANSIENT_SHARE) {
      if (run === null) run = { from: index, to: index };
      else run.to = index;
    } else if (run !== null) {
      bangs.push(run);
      run = null;
    }
  }
  if (run !== null) bangs.push(run);

  const wanted = align === 'last' ? bangs[bangs.length - 1] : bangs[0];
  if (wanted === undefined) return { peakAt: 0, gain: SAMPLE_PEAK / loudest };

  const from = wanted.from * frame;
  const to = Math.min(samples.length, (wanted.to + 1) * frame);

  /* The attack of that one, to the sample. */
  let within = 0;
  for (let index = from; index < to; index += 1) {
    const value = Math.abs(samples[index] ?? 0);
    if (value > within) within = value;
  }

  let at = from / rate;
  for (let index = from; index < to; index += 1) {
    if (Math.abs(samples[index] ?? 0) >= within * ONSET_SHARE) {
      at = index / rate;
      break;
    }
  }

  /*
    Levelled on the bang being synchronised rather than on the file.

    It is the one a listener registers, so it is the one that has to sit with the
    rest of the product — and on a recording whose loudest moment is somewhere
    else entirely, levelling by the file leaves the bang you can hear far quieter
    than the one you cannot. The ceiling is what stops that turning a quiet
    transient up so far that the loud one distorts.
  */
  return {
    peakAt: Math.max(0, at - ONSET_BACKOFF),
    gain: Math.min(SAMPLE_PEAK / within, SAMPLE_CEILING / loudest),
  };
}

function loadSample(audio: AudioContext, url: string, align: Align): Promise<Sample | null> {
  const already = decoding.get(url);
  if (already) return already;

  const job = fetch(url)
    .then((answer) => (answer.ok ? answer.arrayBuffer() : Promise.reject(new Error('missing'))))
    .then((bytes) => audio.decodeAudioData(bytes))
    .then((buffer) => {
      const sample: Sample = { buffer, ...measure(buffer, align) };
      decoded.set(url, sample);
      return sample;
    })
    .catch(() => null);

  decoding.set(url, job);
  return job;
}

/**
 * Play a recording so that its transient lands at a given moment.
 *
 * **This is the whole reason recordings are scheduled rather than fired.** Both
 * of the case's are most of half a second of handling before the clips go, so
 * starting one at the instant a lid moves puts the crack half a second behind
 * the picture — which reads as the animation being broken rather than the sound
 * being late. Started early by exactly its own lead-in, the handling noise
 * covers the case being carried and the crack lands on the frame it opens.
 *
 * The audio clock does the work, not a timer: `start(when)` is sample-accurate
 * where a `setTimeout` is at the mercy of whatever else the main thread is doing
 * — and what else the main thread is doing, at that exact moment, is animating a
 * case.
 *
 * If the moment is too near to fit the lead-in, the front of the file is skipped
 * instead so the transient is still on time. Late is the one thing it may not
 * be.
 */
function playSample(audio: AudioContext, sample: Sample, peakInSeconds: number): void {
  const source = audio.createBufferSource();
  const gain = audio.createGain();

  source.buffer = sample.buffer;
  gain.gain.setValueAtTime(sample.gain, audio.currentTime);
  source.connect(gain).connect(audio.destination);

  const earliest = audio.currentTime + 0.005;
  const wanted = earliest + peakInSeconds;

  let when = wanted - sample.peakAt;
  let from = 0;

  if (when < earliest) {
    from = earliest - when;
    when = earliest;
  }

  source.start(when, from);
}

/**
 * Pull the recordings down before anything needs them.
 *
 * A sample cannot play the first time it is asked for — it has to be fetched and
 * decoded — and the first time is a case being opened, which is the one moment
 * the sound has to be in step with the picture. So the screens that use them ask
 * for them on the way in.
 *
 * With no names it primes every recording there is, which is what enabling sound
 * does: two files, and the reader has just told us they want to hear them.
 */
export function prepareSound(...names: readonly SoundName[]): void {
  if (!soundIsEnabled) return;

  const audio = openAudioContext();
  if (!audio) return;

  const wanted = names.length > 0 ? names : (Object.keys(SAMPLES) as SoundName[]);
  for (const name of wanted) {
    const sample = SAMPLES[name];
    if (sample !== undefined && !decoded.has(sample.url)) {
      void loadSample(audio, sample.url, sample.align);
    }
  }
}

/**
 * Make a sound, optionally at a moment that has not arrived yet.
 *
 * `peakInSeconds` is how far ahead the thing being sounded actually happens —
 * the frame a lid starts to move, the frame it meets the base. Nothing else in
 * this product needs it and it defaults to nothing, which means *now*.
 *
 * **Scheduling ahead is the only way to be on time here.** A recording of a case
 * is half a second of handling and then a crack; played at the moment of the
 * crack it is half a second late, and played early by guesswork it is early by
 * however wrong the guess was. The caller knows exactly when the picture gets
 * there because the animation is a decay with a rate — so it says, and the audio
 * clock does the rest.
 *
 * Which is also why this is not a `setTimeout` around the existing call.
 * `start(when)` is sample-accurate; a timer fires whenever the main thread next
 * has a moment, and what the main thread is doing at that exact moment is
 * animating a case.
 */
export function playSound(name: SoundName, peakInSeconds = 0): void {
  if (!soundIsEnabled) return;

  const audio = openAudioContext();
  if (!audio) return;

  /*
   * The recording if there is one and it is ready; the synthesised voice if it
   * is not.
   *
   * Falling through rather than waiting is the whole of the policy: a sound that
   * arrives late is worse than a different sound that arrives on time. So the
   * first press gets the synthesised crack and starts the download; every press
   * after that gets the recording. Both are placed at the same moment, so which
   * one a reader hears never changes *when* they hear it.
   */
  const sampled = SAMPLES[name];
  if (sampled !== undefined) {
    const ready = decoded.get(sampled.url);
    if (ready) {
      try {
        playSample(audio, ready, peakInSeconds);
      } catch {
        soundIsEnabled = false;
      }
      return;
    }
    void loadSample(audio, sampled.url, sampled.align);
  }

  const voice = SOUND_LIBRARY[name];

  try {
    /* The synthesised voices attack immediately, so their peak is their start. */
    const playheadSeconds = audio.currentTime + 0.005 + peakInSeconds;
    for (const layer of voice.tones) playToneLayer(audio, layer, playheadSeconds);
    for (const layer of voice.noises ?? []) playNoiseLayer(audio, layer, playheadSeconds);
  } catch {
    soundIsEnabled = false;
  }
}

export function soundEnabled(): boolean {
  return soundIsEnabled;
}

export function setSoundEnabled(enabled: boolean): void {
  soundIsEnabled = enabled;

  try {
    window.localStorage.setItem(SOUND_PREFERENCE_STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    /* empty */
  }

  /*
   * Somebody who has just switched sound on is somebody about to hear something,
   * and the recordings are the only voices in here that are not instant.
   */
  if (enabled) {
    openAudioContext();
    prepareSound();
  }

  for (const listener of soundPreferenceListeners) listener(enabled);
}

export function useSoundPreference(): { enabled: boolean; toggle: () => void } {
  const [enabled, setEnabled] = useState(soundIsEnabled);

  useEffect(() => {
    soundPreferenceListeners.add(setEnabled);
    setEnabled(soundIsEnabled);
    return () => {
      soundPreferenceListeners.delete(setEnabled);
    };
  }, []);

  const toggle = useCallback(() => {
    const next = !soundEnabled();
    setSoundEnabled(next);
    if (next) playSound('targetPressed');
  }, []);

  return { enabled, toggle };
}

export function useSoundOnMount(name: SoundName, whenReady = true): void {
  useEffect(() => {
    if (!whenReady) return;
    playSound(name);
  }, [name, whenReady]);
}

export const soundedPointerHandlers = {
  onPointerEnter: () => playSound('pointerCrossedTarget'),
  onPointerDown: () => playSound('targetPressed'),
} as const;
